import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Check, Ticket, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ticketTiers, sponsorTiers } from '../data/mock';

/* Bespoke sponsor-tier insignia · not lucide icons */
const SilverMark = ({ className = 'w-full h-full' }) => (
  <svg viewBox="0 0 48 48" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="silverGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F8FAFC" />
        <stop offset="50%" stopColor="#CBD5E1" />
        <stop offset="100%" stopColor="#64748B" />
      </linearGradient>
      <linearGradient id="silverShine" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.7" />
        <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0" />
      </linearGradient>
    </defs>
    {/* Hexagonal signet */}
    <path d="M24 3 L42 13.5 L42 34.5 L24 45 L6 34.5 L6 13.5 Z" fill="url(#silverGrad)" stroke="#E2E8F0" strokeWidth="0.75" />
    {/* inner hex */}
    <path d="M24 10 L36 17 L36 31 L24 38 L12 31 L12 17 Z" fill="none" stroke="#64748B" strokeWidth="0.5" opacity="0.5" />
    {/* etched S */}
    <path d="M28 20 Q28 17 24.5 17 L21 17 Q18 17 18 20 Q18 22.5 21 23 L27 25 Q30 25.5 30 28 Q30 31 26.5 31 L23 31 Q20 31 20 28" stroke="#334155" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    {/* shine overlay */}
    <path d="M24 3 L42 13.5 L42 34.5 L24 45 L6 34.5 L6 13.5 Z" fill="url(#silverShine)" />
  </svg>
);

const GoldMark = ({ className = 'w-full h-full' }) => (
  <svg viewBox="0 0 48 48" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="goldGrad" cx="0.35" cy="0.35" r="0.75">
        <stop offset="0%" stopColor="#FEF3C7" />
        <stop offset="40%" stopColor="#FBBF24" />
        <stop offset="100%" stopColor="#92400E" />
      </radialGradient>
      <linearGradient id="goldShine" x1="0" y1="0" x2="0.8" y2="0.8">
        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.6" />
        <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0" />
      </linearGradient>
    </defs>
    {/* sunburst rays */}
    {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
      <rect
        key={deg}
        x="23.25"
        y="1.5"
        width="1.5"
        height="6"
        fill="#F59E0B"
        opacity="0.85"
        transform={`rotate(${deg} 24 24)`}
      />
    ))}
    {/* outer coin */}
    <circle cx="24" cy="24" r="16" fill="url(#goldGrad)" stroke="#B45309" strokeWidth="0.6" />
    {/* inner disc */}
    <circle cx="24" cy="24" r="11" fill="none" stroke="#78350F" strokeWidth="0.8" opacity="0.6" />
    {/* centered star mark */}
    <path
      d="M24 16 L26 22 L32 22 L27 25.5 L29 31.5 L24 28 L19 31.5 L21 25.5 L16 22 L22 22 Z"
      fill="#78350F"
      opacity="0.85"
    />
    {/* shine */}
    <circle cx="24" cy="24" r="16" fill="url(#goldShine)" />
  </svg>
);

const DiamondMark = ({ className = 'w-full h-full' }) => (
  <svg viewBox="0 0 48 48" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dCrown1" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F5F3FF" />
        <stop offset="100%" stopColor="#C4B5FD" />
      </linearGradient>
      <linearGradient id="dCrown2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#DDD6FE" />
        <stop offset="100%" stopColor="#A78BFA" />
      </linearGradient>
      <linearGradient id="dPav1" x1="0.5" y1="0" x2="0.5" y2="1">
        <stop offset="0%" stopColor="#A78BFA" />
        <stop offset="100%" stopColor="#5B21B6" />
      </linearGradient>
      <linearGradient id="dPav2" x1="0.5" y1="0" x2="0.5" y2="1">
        <stop offset="0%" stopColor="#8B5CF6" />
        <stop offset="100%" stopColor="#4C1D95" />
      </linearGradient>
    </defs>
    {/* Top crown facets */}
    <polygon points="24,4 8,18 16,18" fill="url(#dCrown2)" />
    <polygon points="24,4 16,18 24,18" fill="url(#dCrown1)" />
    <polygon points="24,4 24,18 32,18" fill="url(#dCrown2)" />
    <polygon points="24,4 32,18 40,18" fill="url(#dCrown1)" />

    {/* Pavilion */}
    <polygon points="8,18 16,18 24,44" fill="url(#dPav2)" />
    <polygon points="16,18 24,18 24,44" fill="url(#dPav1)" />
    <polygon points="24,18 32,18 24,44" fill="url(#dPav2)" />
    <polygon points="32,18 40,18 24,44" fill="url(#dPav1)" />

    {/* Girdle stroke */}
    <line x1="8" y1="18" x2="40" y2="18" stroke="#4C1D95" strokeWidth="0.4" opacity="0.6" />

    {/* Highlight */}
    <polygon points="24,4 16,18 24,18" fill="#FFFFFF" opacity="0.35" />
    <polygon points="16,18 20,26 24,18" fill="#FFFFFF" opacity="0.15" />

    {/* Sparkle */}
    <g>
      <circle cx="38" cy="10" r="1.5" fill="#FFFFFF" opacity="0.9">
        <animate attributeName="opacity" values="0.2;1;0.2" dur="2.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="10" cy="14" r="1" fill="#FFFFFF" opacity="0.7">
        <animate attributeName="opacity" values="0.1;0.9;0.1" dur="2.8s" repeatCount="indefinite" begin="0.6s" />
      </circle>
    </g>
  </svg>
);

const PricingSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const tierMarks = {
    'Silver Sponsor':  SilverMark,
    'Gold Sponsor':    GoldMark,
    'Diamond Sponsor': DiamondMark,
  };

  const tierLimits = {
    'Gold Sponsor': '5',
    'Diamond Sponsor': '2'
  };

  const tierBadges = {
    'Silver Sponsor': { text: 'MOST POPULAR', color: 'bg-[var(--accent-primary)] text-[var(--bg-primary)]' },
    'Diamond Sponsor': { text: 'BEST VALUE', color: 'bg-violet-500 text-white' }
  };

  return (
    <section ref={ref} className="section-padding bg-[var(--bg-secondary)]" id="pricing" data-testid="pricing-section">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center max-w-3xl mx-auto mb-8 md:mb-12"
        >
          <span className="text-xs sm:text-sm text-[var(--accent-primary)] uppercase tracking-widest font-medium">Pricing</span>
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mt-3 sm:mt-4 mb-3 sm:mb-4">
            Get Your Ticket or
            <span className="text-[var(--accent-primary)]"> Become a Sponsor</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-sm md:text-base">
            Early bird pricing available now. Secure your spot before prices increase.
          </p>
        </motion.div>
        
        {/* Solo Ticket */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
          className="max-w-3xl mx-auto mb-10 md:mb-14"
        >
          <h3 className="text-base sm:text-lg font-semibold text-center mb-4 md:mb-5">Solo Attendee Ticket</h3>
          {ticketTiers.map((tier, index) => (
            <div
              key={index}
              className="p-4 sm:p-5 md:p-6 rounded-2xl bg-[var(--bg-primary)] border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                {/* Left - Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[var(--accent-bg)] flex items-center justify-center">
                      <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--accent-primary)]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm sm:text-base">{tier.name}</h4>
                      <p className="text-[10px] sm:text-xs text-[var(--text-muted)]">{tier.subtitle}</p>
                    </div>
                  </div>
                  
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-[10px] sm:text-xs text-[var(--text-secondary)]">
                        <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[var(--accent-primary)] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Right - Pricing */}
                <div className="flex flex-row md:flex-col items-center md:items-end gap-3 md:gap-2 pt-3 md:pt-0 border-t md:border-t-0 border-[var(--border-subtle)]">
                  <div className="text-right">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl sm:text-2xl font-bold text-[var(--accent-primary)]">${tier.earlyBird}</span>
                      <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">early bird</span>
                    </div>
                  </div>
                  <Button className="h-9 sm:h-10 px-4 sm:px-6 rounded-xl font-medium text-xs sm:text-sm whitespace-nowrap" asChild>
                    <a
                      href="https://luma.com/6kdsrxks"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="pricing-get-ticket-btn"
                    >
                      Get Ticket
                      <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1.5" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
        
        {/* Sponsor Tiers */}
        <h3 className="text-base sm:text-lg font-semibold text-center mb-4 md:mb-5">Sponsorship Packages</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto">
          {sponsorTiers.map((tier, index) => {
            const Mark = tierMarks[tier.name] || SilverMark;
            const limit = tierLimits[tier.name];
            const badge = tierBadges[tier.name];
            const isSilver = tier.name === 'Silver Sponsor';
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 + index * 0.1 }}
                className={`relative p-4 sm:p-5 rounded-2xl bg-[var(--bg-primary)] border transition-colors flex flex-col ${
                  isSilver 
                    ? 'border-2 border-[var(--accent-primary)]' 
                    : 'border-[var(--border-subtle)] hover:border-[var(--accent-primary)]'
                }`}
              >
                {/* Badges */}
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  {badge && (
                    <span className={`px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold ${badge.color}`}>
                      {badge.text}
                    </span>
                  )}
                  {!badge && <span />}
                  
                  {limit && (
                    <span className={`px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold ${
                      tier.name === 'Gold Sponsor' 
                        ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                        : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                    }`}>
                      LIMITED TO {limit}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 sm:gap-3 mb-3">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-white/2 border border-white/8 p-1.5 sm:p-2">
                    <Mark />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm sm:text-base">{tier.name}</h4>
                    <p className="text-[10px] sm:text-xs text-[var(--text-muted)]">{tier.subtitle}</p>
                  </div>
                </div>
                
                {/* Pricing */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-xl sm:text-2xl font-bold text-[var(--accent-primary)]">${tier.earlyBird.toLocaleString()}</span>
                    <span className="text-[10px] sm:text-xs text-[var(--text-muted)]">early bird</span>
                  </div>
                </div>
                
                <ul className="space-y-1.5 mb-4 flex-grow">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[10px] sm:text-xs text-[var(--text-secondary)]">
                      <Check className="w-3 h-3 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  asChild
                  className="w-full h-9 sm:h-10 rounded-xl font-semibold text-xs sm:text-sm bg-[var(--accent-primary)] text-[var(--bg-primary)] hover:bg-[var(--accent-hover)] mt-auto"
                >
                  <a
                    href="https://luma.com/6kdsrxks"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`sponsor-${tier.name.toLowerCase().replace(/\s+/g,'-')}-btn`}
                  >
                    Become Sponsor
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1.5" />
                  </a>
                </Button>
              </motion.div>
            );
          })}
        </div>
        
        {/* Sponsorship approval note */}
        <p className="text-center text-[10px] sm:text-xs text-[var(--text-muted)] mt-5 max-w-2xl mx-auto">
          All sponsorships and attendees are subject to approval. We may require additional information to ensure a quality onboarding & matchmaking experience.
        </p>
        
        {/* Payment methods */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 md:mt-10"
        >
          <p className="text-xs sm:text-sm text-[var(--text-muted)] mb-3">We accept payments via</p>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            <div className="flex items-center px-3 py-2 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-subtle)]">
              <img 
                src="https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/n9bmrhem_4%20cc%20logos.svg"
                alt="Credit Cards Accepted"
                className="h-5 sm:h-6 w-auto"
              />
            </div>
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-subtle)]">
              <img 
                src="https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/ay7n497i_Usdc--Streamline-Cryptocurrency.svg"
                alt="USDC"
                className="w-5 h-5"
              />
              <span className="text-xs font-medium text-white">USDC</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-subtle)]">
              <img 
                src="https://customer-assets.emergentagent.com/job_6f758192-ab9f-49b2-b18c-e8fbed640b66/artifacts/xkims4id_Usdt--Streamline-Cryptocurrency.svg"
                alt="USDT"
                className="w-5 h-5"
              />
              <span className="text-xs font-medium text-white">USDT</span>
            </div>
          </div>
          <p className="text-[10px] sm:text-xs text-[var(--text-muted)] mt-3">
            Need help? Contact us at <a href="mailto:hi@zedit.io" className="text-[var(--accent-primary)] hover:underline">hi@zedit.io</a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
